import { useEffect, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { createSignalRConnection } from '../services/api';
import type {
  JobApprovedEvent, QuoteReceivedEvent, ContractGeneratedEvent,
  PaymentReleasedEvent, CarrierLocationUpdate, ChatMessage, BazaarChatMessage,
} from '../types';
import type { SupportEmailSummary } from '../types/mailbox';

// ─────────────────────────────────────────────
//  BASE HUB HOOK
// ─────────────────────────────────────────────
function useSignalRHub(
  hubPath: string,
  getToken: () => Promise<string>,
  onConnected?: (conn: signalR.HubConnection) => void,
  enabled = true,
  transport?: signalR.HttpTransportType
) {
  const connRef     = useRef<signalR.HubConnection | null>(null);
  // Keep a stable ref to the latest onConnected so reconnect can call it too
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;

  useEffect(() => {
    if (!enabled) return;

    const conn = createSignalRConnection(hubPath, getToken, transport);
    connRef.current = conn;

    const join = () => onConnectedRef.current?.(conn);

    conn.start()
      .then(() => {
        console.log(`[SignalR] Connected to ${hubPath}`);
        join();
      })
      .catch((err) => console.error(`[SignalR] Failed to connect to ${hubPath}:`, err));

    // Re-join groups on every reconnect (after network drops etc.)
    conn.onreconnected(() => {
      console.log(`[SignalR] Reconnected to ${hubPath}`);
      join();
    });

    return () => { conn.stop().catch(console.error); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubPath, enabled]);

  return connRef;
}

// ─────────────────────────────────────────────
//  JOB HUB HOOK
//  Used by: customer job detail page, driver feed
// ─────────────────────────────────────────────
export interface LiveCarrierLocation {
  carrierId: string;
  jobId: string;
  lat: number;
  lng: number;
  accuracy?: number;
  recordedAt: string;
}

interface UseJobHubOptions {
  getToken: () => Promise<string>;
  enabled?: boolean;
  jobId?: string;
  vehicleType?: string;
  areaCode?: string;
  onJobApproved?: (e: JobApprovedEvent) => void;
  onQuoteReceived?: (e: QuoteReceivedEvent) => void;
  /** Fired when a customer accepts a quote — backend sends "QuoteAccepted" event. */
  onQuoteAccepted?: (e: QuoteReceivedEvent) => void;
  onContractGenerated?: (e: ContractGeneratedEvent) => void;
  onPaymentReleased?: (e: PaymentReleasedEvent) => void;
  onNewJobAvailable?: (job: unknown) => void;
  onJobAlertMatch?: (e: unknown) => void;
  /** Fired when the assigned carrier posts a GPS update while the job is InProgress. */
  onCarrierLocationUpdated?: (e: LiveCarrierLocation) => void;
  /** Fired when the carrier explicitly accepts the job after contract generation (sets carrierAcceptedAt). */
  onCarrierAcceptedJob?: (e: unknown) => void;
  /** Fired when the carrier starts the job — status transitions to InProgress. */
  onJobStarted?: (e: unknown) => void;
  /** Fired when the carrier marks the job complete. */
  onJobCompleted?: (e: unknown) => void;
  /** Fired when the carrier pauses the job. */
  onJobPaused?: (e: unknown) => void;
  /** Fired when the carrier resumes the job. */
  onJobResumed?: (e: unknown) => void;
  /** Fired when the carrier submits proof of delivery. */
  onJobPodSubmitted?: (e: unknown) => void;
}

export function useJobHub({
  getToken, enabled = true, jobId, vehicleType, areaCode,
  onJobApproved, onQuoteReceived, onQuoteAccepted, onContractGenerated,
  onPaymentReleased, onNewJobAvailable, onJobAlertMatch, onCarrierLocationUpdated,
  onCarrierAcceptedJob, onJobStarted, onJobCompleted, onJobPaused, onJobResumed, onJobPodSubmitted,
}: UseJobHubOptions) {
  // Keep latest callbacks in refs — handlers registered on the connection stay
  // stable and always delegate to the most-recent prop, so we never need to
  // de-register / re-register on every render.
  const cbRef = useRef({ onJobApproved, onQuoteReceived, onQuoteAccepted, onContractGenerated,
                         onPaymentReleased, onNewJobAvailable, onJobAlertMatch, onCarrierLocationUpdated,
                         onCarrierAcceptedJob, onJobStarted, onJobCompleted, onJobPaused, onJobResumed, onJobPodSubmitted });
  cbRef.current = { onJobApproved, onQuoteReceived, onQuoteAccepted, onContractGenerated,
                    onPaymentReleased, onNewJobAvailable, onJobAlertMatch, onCarrierLocationUpdated,
                    onCarrierAcceptedJob, onJobStarted, onJobCompleted, onJobPaused, onJobResumed, onJobPodSubmitted };

  useSignalRHub('/hubs/jobs', getToken, (conn) => {
    if (jobId) conn.invoke('TrackJob', jobId).catch(console.error);
    if (vehicleType && areaCode)
      conn.invoke('JoinJobFeedGroup', vehicleType, areaCode).catch(console.error);

    // Register handlers here so they are always wired up on connect AND reconnect,
    // regardless of when `enabled` becomes true relative to component mount.
    // conn.off() first prevents duplicate listeners on reconnect.
    conn.off('JobApproved');
    conn.on('JobApproved',              (e) => cbRef.current.onJobApproved?.(e));
    conn.off('QuoteReceived');
    conn.on('QuoteReceived',            (e) => cbRef.current.onQuoteReceived?.(e));
    conn.off('QuoteAccepted');
    conn.on('QuoteAccepted',            (e) => cbRef.current.onQuoteAccepted?.(e));
    conn.off('ContractGenerated');
    conn.on('ContractGenerated',        (e) => cbRef.current.onContractGenerated?.(e));
    conn.off('PaymentReleased');
    conn.on('PaymentReleased',          (e) => cbRef.current.onPaymentReleased?.(e));
    conn.off('NewJobAvailable');
    conn.on('NewJobAvailable',          (e) => cbRef.current.onNewJobAvailable?.(e));
    conn.off('JobAlertMatch');
    conn.on('JobAlertMatch',            (e) => cbRef.current.onJobAlertMatch?.(e));
    conn.off('CarrierLocationUpdated');
    conn.on('CarrierLocationUpdated',   (e) => cbRef.current.onCarrierLocationUpdated?.(e));
    conn.off('CarrierAcceptedJob');
    conn.on('CarrierAcceptedJob',       (e) => cbRef.current.onCarrierAcceptedJob?.(e));
    conn.off('JobStarted');
    conn.on('JobStarted',               (e) => cbRef.current.onJobStarted?.(e));
    conn.off('JobCompleted');
    conn.on('JobCompleted',             (e) => cbRef.current.onJobCompleted?.(e));
    conn.off('JobPaused');
    conn.on('JobPaused',                (e) => cbRef.current.onJobPaused?.(e));
    conn.off('JobResumed');
    conn.on('JobResumed',               (e) => cbRef.current.onJobResumed?.(e));
    conn.off('JobPodSubmitted');
    conn.on('JobPodSubmitted',          (e) => cbRef.current.onJobPodSubmitted?.(e));
  }, enabled);
}

// ─────────────────────────────────────────────
//  MESSAGING HUB HOOK
//  Used by: job conversation thread
// ─────────────────────────────────────────────
interface UseMessagingHubOptions {
  getToken: () => Promise<string>;
  jobId: string;
  onMessageReceived: (msg: ChatMessage) => void;
  enabled?: boolean;
}

export function useMessagingHub({ getToken, jobId, onMessageReceived, enabled = true }: UseMessagingHubOptions) {
  const connRef = useSignalRHub(
    '/hubs/messages', getToken,
    (conn) => { conn.invoke('JoinConversation', jobId).catch(console.error); },
    enabled,
    // Force LongPolling: WebSocket gets 429 on Azure App Service; SSE negotiate POST returns 404.
    // LongPolling uses only GET requests and reliably works behind Azure's reverse proxy.
    signalR.HttpTransportType.LongPolling,
  );

  useEffect(() => {
    const conn = connRef.current;
    if (!conn) return;
    conn.on('ReceiveMessage', onMessageReceived);
    return () => conn.off('ReceiveMessage');
  });

  const sendMessage = useCallback(
    async (recipientId: string, content: string) => {
      await connRef.current?.invoke('SendMessage', jobId, recipientId, content);
    },
    [jobId]
  );

  return { sendMessage };
}

// ─────────────────────────────────────────────
//  BAZAAR CHAT HUB HOOK
//  Used by: BazaarChatPanel (listing detail buyer <-> seller chat)
// ─────────────────────────────────────────────
interface UseBazaarMessagingHubOptions {
  getToken: () => Promise<string>;
  listingId: string;
  onMessageReceived: (msg: BazaarChatMessage) => void;
  enabled?: boolean;
}

export function useBazaarMessagingHub({ getToken, listingId, onMessageReceived, enabled = true }: UseBazaarMessagingHubOptions) {
  const connRef = useSignalRHub(
    '/hubs/bazaar-chat', getToken,
    (conn) => { conn.invoke('JoinConversation', listingId).catch(console.error); },
    enabled,
    // Force LongPolling — see useMessagingHub for why (Azure App Service reverse proxy).
    signalR.HttpTransportType.LongPolling,
  );

  useEffect(() => {
    const conn = connRef.current;
    if (!conn) return;
    conn.on('ReceiveMessage', onMessageReceived);
    return () => conn.off('ReceiveMessage');
  });
}

// ─────────────────────────────────────────────
//  RELAY HUB HOOK
//  Used by: relay tracking view, ops dashboard
// ─────────────────────────────────────────────
interface UseRelayHubOptions {
  getToken: () => Promise<string>;
  jobId: string;
  onCarrierLocationUpdate: (update: CarrierLocationUpdate) => void;
  onRelayHandoverAlert?: (data: { segmentId: string }) => void;
}

export function useRelayHub({
  getToken, jobId, onCarrierLocationUpdate, onRelayHandoverAlert,
}: UseRelayHubOptions) {
  const connRef = useSignalRHub('/hubs/relay', getToken, (conn) => {
    conn.invoke('TrackRelayJob', jobId).catch(console.error);
  });

  useEffect(() => {
    const conn = connRef.current;
    if (!conn) return;
    conn.on('CarrierLocationUpdate', onCarrierLocationUpdate);
    if (onRelayHandoverAlert) conn.on('RelayHandoverAlert', onRelayHandoverAlert);
    return () => {
      conn.off('CarrierLocationUpdate');
      conn.off('RelayHandoverAlert');
    };
  });

  const updateLocation = useCallback(
    async (lat: number, lng: number) => {
      await connRef.current?.invoke('UpdateDriverLocation', jobId, lat, lng);
    },
    [jobId]
  );

  return { updateLocation };
}

// ─────────────────────────────────────────────
//  useJobMessageHub  — simple alias
//  Used by MessagingPanel for real-time chat.
// ─────────────────────────────────────────────
export function useJobMessageHub(
  getToken: () => Promise<string>,
  jobId: string,
  onMessage: (msg: ChatMessage) => void
) {
  useMessagingHub({ getToken, jobId, onMessageReceived: onMessage });
}

// ─────────────────────────────────────────────
//  VOICE HUB HOOK
//  Used by: VoiceChatbotPage
//  Provides real-time phase status while the backend runs
//  Whisper STT → LLM → Piper TTS.
//  Gracefully no-ops if the hub is unreachable; HTTP path still works.
// ─────────────────────────────────────────────
interface UseVoiceHubOptions {
  sessionId: string;
  getToken?: () => Promise<string>;
  onVoiceStatus?: (phase: string) => void;
  onVoiceError?: (msg: string) => void;
}

export function useVoiceHub({
  sessionId,
  getToken = () => Promise.resolve(''),
  onVoiceStatus,
  onVoiceError,
}: UseVoiceHubOptions) {
  const connRef = useSignalRHub('/hubs/voice', getToken, (conn) => {
    conn.invoke('JoinSession', sessionId).catch(console.error);
  });

  useEffect(() => {
    const conn = connRef.current;
    if (!conn) return;
    if (onVoiceStatus) conn.on('VoiceStatus', onVoiceStatus);
    if (onVoiceError)  conn.on('VoiceError',  onVoiceError);
    return () => {
      conn.off('VoiceStatus');
      conn.off('VoiceError');
    };
  });
}

// ─────────────────────────────────────────────
//  ADMIN HUB HOOK
//  Used by: EmailMonitoringPage
//  Event: 'NewSupportEmail' — pushed by MailboxMonitorBackgroundService
//  Hub path: /hubs/admin  (requires Admin/Ops role)
// ─────────────────────────────────────────────
interface UseAdminHubOptions {
  getToken:          () => Promise<string>;
  onNewSupportEmail?: (email: SupportEmailSummary) => void;
}

export function useAdminHub({ getToken, onNewSupportEmail }: UseAdminHubOptions) {
  const connRef = useSignalRHub('/hubs/admin', getToken);

  useEffect(() => {
    const conn = connRef.current;
    if (!conn) return;
    if (onNewSupportEmail) conn.on('NewSupportEmail', onNewSupportEmail);
    return () => { conn.off('NewSupportEmail'); };
  });
}

// ─────────────────────────────────────────────
//  FLEET HUB HOOK
//  Used by: FleetMapPage (carrier/haulier watching all drivers)
//  Event: 'DriverLocationUpdated' — pushed by ApiSignalRNotifier
// ─────────────────────────────────────────────
export interface FleetLocationUpdate {
  carrierId: string;
  jobId?: string | null;
  lat: number;
  lng: number;
  accuracy?: number;
  recordedAt: string;
}

interface UseFleetHubOptions {
  getToken:                 () => Promise<string>;
  carrierId:                string;
  onCarrierLocationUpdated:  (update: FleetLocationUpdate) => void;
  enabled?:                 boolean;
}

export function useFleetHub({
  getToken, carrierId, onCarrierLocationUpdated, enabled = true,
}: UseFleetHubOptions) {
  const cbRef = useRef(onCarrierLocationUpdated);
  cbRef.current = onCarrierLocationUpdated;

  useSignalRHub('/hubs/jobs', getToken, (conn) => {
    if (carrierId) conn.invoke('JoinFleetGroup', carrierId).catch(console.error);
    conn.off('CarrierLocationUpdated');
    conn.on('CarrierLocationUpdated', (payload: FleetLocationUpdate) => cbRef.current(payload));
  }, enabled);
}
